import { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import Analytics from '@/analyticsGA';
import { generateUniqId } from '@/helpers/uuid';
import { Badge, Button } from '@scramble/ui';
import { useBrandSelectionStatus } from '@/pages/InvestingPage/hooks/useBrandSelectionStatus';
import { Image } from '@/components/Image';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { useWindowSize } from '@/hooks/useWindowSize';
import { MODAL_IDS } from '@/kit/Modal/keys';
import IconInfo from '@scramble/design-system/icons/info-circle.svg';
import IconArrow from '@scramble/design-system/icons/chevron-up.svg';
import { prettyFloatMoney } from '@/helpers/utils';
import s from './index.module.scss';

const id = MODAL_IDS.brands_filled_for_investment;

const SHOW_BRAND_NUMBER = 2;

export const BrandsForInvestmentFilledModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

const Modal = ({
  wizardId,
  allocation,
  remainingBrands,
  onBrandsClick,
  onContinue,
}) => {
  const [expand, setExpand] = useState(false);
  const { isMobile } = useWindowSize();
  const { closeModal } = useModal();
  const { selectedAndFilledBrandList, hasData } = useBrandSelectionStatus();
  const { t } = useTranslation('modal');
  const modalId = useRef(generateUniqId());

  useEffect(() => {
    hasData &&
      Analytics.brandsFilledShow({
        modal_id: modalId.current,
        wizard_id: wizardId,
        filled_brands_count: selectedAndFilledBrandList.length,
      });
  }, [hasData, selectedAndFilledBrandList]);

  useEffect(() => {
    const backdrop = document.querySelector('[data-role="modal-backdrop"]');
    const closeX = document.querySelector('[data-role="modal-close-x"]');
    const backdropHandler = () => {
      Analytics.brandsFilledModalClosed({
        wizard_id: wizardId,
        modal_id: modalId.current,
        exit_type: 'outside_click',
      });
    };
    const closeXHandler = () => {
      Analytics.brandsFilledModalClosed({
        wizard_id: wizardId,
        modal_id: modalId.current,
        exit_type: 'x_close',
      });
    };
    backdrop?.addEventListener('click', backdropHandler);
    closeX?.addEventListener('click', closeXHandler);
    return function cleanup() {
      backdrop?.removeEventListener('click', backdropHandler);
      closeX?.removeEventListener('click', closeXHandler);
    };
  }, [isMobile]);

  const extraBrands = selectedAndFilledBrandList.length - SHOW_BRAND_NUMBER;

  const content = (
    <div className={s.modal}>
      <div className={s.header}>
        <div className={s.header_icon}>
          <img src={IconInfo} alt="info" width={28} height={28} />
        </div>
        <h1 className="f-headline-2xs f-500">
          {t('brands_filled_inv_modal.title')}
        </h1>
        <p className="f-body-sm">{t('brands_filled_inv_modal.description')}</p>
      </div>
      <div className={s.content}>
        <div className={s.content_scroll}>
          <div className={s.brands}>
            {selectedAndFilledBrandList
              .slice(0, SHOW_BRAND_NUMBER)
              .map(item => (
                <div key={item.id} className={s.brand}>
                  <div className={s.brand_content}>
                    <div className={s.brand_icon}>
                      <Image
                        image={item.logo}
                        preset="selectionLogo"
                        alt={item.name}
                        width={24}
                        height={24}
                      />
                    </div>
                    <div className={classnames('f-body-md', s.brand_name)}>
                      {item.name}
                    </div>
                  </div>
                  <Badge
                    type="neutral"
                    appearance="filled"
                    size="small"
                    label={t('brands_filled_inv_modal.filled')}
                  />
                </div>
              ))}
            {extraBrands > 0 && (
              <div className={classnames(s.more, 'f-body-sm')}>
                {t('brands_filled_inv_modal.more_brands', {
                  count: extraBrands,
                })}
              </div>
            )}
          </div>
        </div>

        <div className={s.info}>
          <div className={s.summary}>
            <div className={s.row}>
              <div className="f-body-md">
                {t('brands_filled_inv_modal.allocation')}
              </div>
              <div className={s.row_right}>
                <div className="f-body-md f-500">
                  {prettyFloatMoney(allocation.total)}
                </div>
                <button
                  type="button"
                  className={classnames(
                    s.btn_expand,
                    expand && s.btn_expand_active,
                  )}
                  onClick={() => setExpand(!expand)}
                >
                  <img src={IconArrow} alt="arrow" width={20} height={20} style={{ minWidth: '20px'}} />
                </button>
              </div>
            </div>
            <div className={classnames(s.groups, expand && s.groups_active)}>
              <div className={s.row}>
                <div className="f-body-md">
                  {t('brands_filled_inv_modal.group_a')}
                </div>
                <div className="f-body-md f-500">
                  {prettyFloatMoney(allocation.conservative)}
                </div>
              </div>
              <div className={s.row}>
                <div className="f-body-md">
                  {t('brands_filled_inv_modal.group_b')}
                </div>
                <div className="f-body-md f-500">
                  {prettyFloatMoney(allocation.moderate)}
                </div>
              </div>
            </div>
            <div className={s.row}>
              <div className="f-body-md">
                {t('brands_filled_inv_modal.remaining_brands')}
              </div>
              <div className="f-body-md f-500">{remainingBrands}</div>
            </div>
          </div>
          <div className={s.warning}>
            <img
              src={IconInfo}
              alt="info"
              width={20}
              height={20}
              style={{ minWidth: '20px' }}
            />
            <div className="f-body-sm">
              {t('brands_filled_inv_modal.warning')}
            </div>
          </div>
        </div>
      </div>
      <div className={s.footer}>
        <Button
          theme="neutral"
          appearance="tonal"
          size="lg"
          onClick={() => {
            Analytics.brandsFilledModalClosed({
              wizard_id: wizardId,
              modal_id: modalId.current,
              exit_type: 'add_button',
            });
            closeModal(id);
            onBrandsClick();
          }}
        >
          {t('brands_filled_inv_modal.all_brands')}
        </Button>
        <Button
          theme="neutral"
          appearance="filled"
          onClick={() => {
            Analytics.brandsFilledModalClosed({
              wizard_id: wizardId,
              modal_id: modalId.current,
              exit_type: 'continue_button',
            });
            closeModal(id);
            onContinue();
          }}
        >
          {t('brands_filled_inv_modal.continue')}
        </Button>
      </div>
    </div>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};
